import { create } from 'zustand';
import { ChartSettings, ChartSettingsField, ChartSettingsStore, ChartStyle, ChartFeatures } from './types';
import { getStoredChartSettings, saveChartSettings } from './storage';

export const useChartSettingsStore = create<ChartSettingsStore>((set, get) => ({
  chartSettings: getStoredChartSettings(),

  updateChartSetting: <K extends ChartSettingsField>(field: K, value: ChartSettings[K]) => {
    const currentSettings = get().chartSettings;
    const updatedSettings = {
      ...currentSettings,
      [field]: value,
    };

    // Update store
    set({ chartSettings: updatedSettings });

    // Save to localStorage
    saveChartSettings(updatedSettings);
  },

  updateChartSettingsStyle: <K extends keyof ChartStyle>(field: K, value: ChartStyle[K]) => {
    const currentSettings = get().chartSettings;
    const updatedSettings = {
      ...currentSettings,
      chartStyle: {
        ...currentSettings.chartStyle,
        [field]: value,
      },
    };

    // Update store
    set({ chartSettings: updatedSettings });

    // Save to localStorage
    saveChartSettings(updatedSettings);
  },

  updateMultipleChartSettingsStyle: (updates: Partial<ChartStyle>) => {
    const currentSettings = get().chartSettings;
    const updatedSettings = {
      ...currentSettings,
      chartStyle: {
        ...currentSettings.chartStyle,
        ...updates,
      },
    };

    // Update store
    set({ chartSettings: updatedSettings });

    // Save to localStorage
    saveChartSettings(updatedSettings);
  },

  updateChartFeature: <K extends keyof ChartFeatures>(field: K, value: ChartFeatures[K]) => {
    const currentSettings = get().chartSettings;
    let updatedFeatures = {
      ...currentSettings.features,
      [field]: value,
    };

    // Ensure only one volume type is enabled at a time
    if (field === 'volumes' && value === true) {
      updatedFeatures.tickVolumes = false;
    } else if (field === 'tickVolumes' && value === true) {
      updatedFeatures.volumes = false;
    }

    const updatedSettings = {
      ...currentSettings,
      features: updatedFeatures,
    };

    // Update store
    set({ chartSettings: updatedSettings });

    // Save to localStorage
    saveChartSettings(updatedSettings);
  },

  getChartSetting: <K extends ChartSettingsField>(field: K): ChartSettings[K] => {
    return get().chartSettings[field];
  },

  loadChartSettings: () => {
    const chartSettings = getStoredChartSettings();
    set({ chartSettings });
  },
}));
